import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Tag } from '../../../common/components/detail/detail';
import { Gate } from '../../../global/contexts/auth/auth';
import { useFleet, useFleetCatalog, useReviewWork } from '../contexts/fleet';
import { fleetCounts, machineGroups, orderCandidates, projectGroups } from '../utils/fleet';
import { positiveId } from '../utils/operator';
import ActionModal from './action-modal';
import AgentRow from './agent-row';
import BatchModal from './batch-modal';
import { RawJson, RefreshStatus } from './bits';

function GroupActions({ rows, label, pauseLabel = 'Pausar grupo', disabled, onAct }) {
    const canEnable = orderCandidates(rows, 'habilitar').length > 0;
    const canPause = orderCandidates(rows, 'pausar_tomas').length > 0;
    return <div className="coord-fleet__actions">
        <Gate all={['machines:admin', 'tokens:admin']}>
            <button type="button" className="coord__button coord__button--primary" disabled={disabled || !canEnable}
                onClick={() => onAct('habilitar')}>{label}</button>
        </Gate>
        <Gate domain="machines:admin">
            <button type="button" className="coord__button" disabled={disabled || !canPause}
                onClick={() => onAct('pausar_tomas')}>{pauseLabel}</button>
        </Gate>
    </div>;
}

function MachineCard({ machine, groups, repositories, reviewWork, observedAt, disabled, onGroup, onSingle, filtered }) {
    const rows = groups.flatMap(group => group.rows);
    const counts = fleetCounts(rows, reviewWork);
    return <li className="coord-machine">
        <header className="coord-machine__head">
            <div className="coord-machine__identity">
                <svg aria-hidden="true" viewBox="0 0 24 24" className="coord-machine__icon"><rect x="3" y="4" width="18" height="13" rx="2" /><path d="M8 21h8m-4-4v4" /></svg>
                <div><h3>{machine.name}</h3><p>{counts.total} agentes · {counts.connected} conectados · {counts.working} con trabajo tomado</p></div>
            </div>
            <Tag tone={counts.connected === counts.total ? 'active' : 'warn'}>
                {counts.connected === counts.total ? 'Conectado' : counts.connected ? 'Conexión parcial' : 'Sin conexión confirmada'}
            </Tag>
        </header>
        <div className="coord-machine__control">
            <span>{counts.enabled} de {counts.total} agentes con autorización confirmada</span>
            <GroupActions rows={rows} label={filtered ? 'Autorizar agentes visibles' : 'Autorizar equipo'}
                pauseLabel={filtered ? 'Pausar agentes visibles' : 'Pausar equipo'} disabled={disabled}
                onAct={verb => onGroup(rows, verb, `${machine.name}${filtered ? ' · proyecto seleccionado' : ' · todos sus agentes'}`)} />
        </div>
        {groups.map(group => <section className="coord-project" key={group.key} aria-label={`${group.name} en ${machine.name}`}>
            <header className="coord-project__head">
                <div><span className="coord-project__eyebrow">Proyecto</span><h4>{group.name}</h4></div>
                <span className="coord__count">{group.rows.length} agentes</span>
            </header>
            <ul className="coord-project__agents">
                {group.rows.map(row => <AgentRow key={row.position_id} row={row} observedAt={observedAt}
                    review={reviewWork?.get(row.position_id)}
                    ordersEnabled={!disabled}
                    repositoryName={repositories.find(repo => repo.id === row.seat.repository_id)?.name ?? `Repositorio #${row.seat.repository_id}`}
                    onAct={action => ['habilitar', 'pausar_tomas'].includes(action.key)
                        ? onGroup([row], action.key, `Agente #${row.position_id} · ${machine.name}`)
                        : onSingle(row, action)} />)}
            </ul>
        </section>)}
        <details className="coord-machine__details"><summary>Detalles del equipo</summary>
            <p>{machine.poolId ? `Pool de Composer ${machine.poolId}. Agrupación visual por el nombre de registro y propietario; cada agente conserva su identidad y permisos.` : 'Equipo registrado en Mastermind.'}</p>
            <RawJson value={machine.rows} label="Ver diagnóstico de los agentes" />
        </details>
    </li>;
}

export default function FleetScreen() {
    const [params, setParams] = useSearchParams();
    const [acting, setActing] = useState(null);
    const [batch, setBatch] = useState(null);
    const query = useFleet();
    // Qué revisión tiene reservada cada puesto. Vive aparte de la parrilla
    // porque sale de otra ruta y de otro permiso: sin `events:read` la
    // pantalla sigue entera y la tarjeta lo dice.
    const reviews = useReviewWork();
    const repositories = useFleetCatalog('repositories');
    const projects = useFleetCatalog('projects');
    const { data, error, isLoading } = query;
    const repositoryList = repositories.data ?? [];
    const projectList = projects.data ?? [];
    const projectId = positiveId(params.get('proyecto'));
    const allRows = data?.items ?? [];
    const groupedProjects = projectGroups(allRows, repositoryList, projectList);
    const selectedProject = groupedProjects.find(group => group.id === projectId);
    const rows = projectId ? selectedProject?.rows ?? [] : allRows;
    // Resolve machine identity over the whole fleet before applying a project filter.
    const machines = machineGroups(allRows).map(machine => ({
        ...machine, visibleRows: machine.rows.filter(row => rows.includes(row)),
    })).filter(machine => machine.visibleRows.length);
    const counts = fleetCounts(rows, reviews.data);
    const disabled = !!error || isLoading || data?.features?.orders === false || data?.features?.positions === false;
    const catalogIncomplete = !!repositories.error || repositories.isLoading;
    const scope = projectId ? `${selectedProject?.name ?? `Proyecto #${projectId}`} · todos sus equipos` : 'Todos los equipos de tu alcance';
    const openGroup = (targets, verb, title) => setBatch({ rows: targets, verb, scope: title, repositories: repositoryList });
    return <section className="coord coord-fleet">
        <header className="coord__head"><div>
            <h2 className="coord__title">Tus equipos</h2>
            <p className="coord__about">Elige qué agentes pueden trabajar por ti, desde un equipo o un proyecto completo.</p>
        </div></header>
        <dl className="coord-fleet__stats" aria-label="Resumen de equipos">
            <div><dt>Equipos</dt><dd>{machines.length}</dd></div>
            <div><dt>Agentes conectados</dt><dd>{counts.connected}<small> / {counts.total}</small></dd></div>
            <div><dt>Autorización confirmada</dt><dd>{counts.enabled}</dd></div>
            <div>
                <dt>Con trabajo tomado</dt>
                <dd>{counts.working}</dd>
                {/* Qué lo compone: una corrida la declara el agente, una ronda
                    reservada la registra Core. Ninguna de las dos prueba que el
                    proceso esté vivo. */}
                <p className="coord-fleet__kpi-note">
                    {counts.running} ejecutando · {counts.reviewing} revisando
                </p>
            </div>
        </dl>
        <div className="coord-fleet__scope">
            <div className="coord__toolbar">
                <Gate domain="repositories:read"><label className="coord__field"><span>Proyecto</span>
                    <select className="coord__select" value={projectId ?? ''} disabled={catalogIncomplete}
                        onChange={event => {
                            const next = new URLSearchParams(params);
                            next.delete('repo'); next.delete('desde');
                            event.target.value ? next.set('proyecto', event.target.value) : next.delete('proyecto');
                            setParams(next);
                        }}>
                        <option value="">Todos los proyectos</option>
                        {groupedProjects.filter(group => group.id != null).map(group => <option key={group.id} value={group.id}>{group.name}</option>)}
                    </select>
                </label></Gate>
                <RefreshStatus query={query} />
            </div>
            <div className="coord-fleet__scope-actions">
                <p><strong>{projectId ? selectedProject?.name ?? `Proyecto #${projectId}` : 'Todo tu pool'}</strong><span>{counts.total} agentes en {machines.length} {machines.length === 1 ? 'equipo' : 'equipos'}</span></p>
                <GroupActions rows={rows} label={projectId ? 'Autorizar proyecto' : 'Autorizar todos'}
                    pauseLabel={projectId ? 'Pausar proyecto' : 'Pausar todos'}
                    disabled={disabled || projectId != null && catalogIncomplete}
                    onAct={verb => openGroup(rows, verb, scope)} />
            </div>
            <p className="coord__panel-note">Trabajarán sobre tareas ya aprobadas. Puedes pausar nuevas tomas sin interrumpir una tarea en curso.</p>
        </div>
        {isLoading && <p role="status">Consultando equipos y agentes…</p>}
        {error && <p role="alert" className="coord__error">{error.message} Los controles quedan deshabilitados hasta actualizar.</p>}
        {(repositories.error || projects.error) && <p role="alert" className="coord__error">No se pudo completar el catálogo de proyectos. {repositories.error?.message ?? projects.error?.message}</p>}
        {data?.features?.positions === false && <p className="coord__unavailable">Los puestos están deshabilitados en el servidor.</p>}
        {data?.features?.orders === false && <p className="coord__unavailable">Las órdenes están deshabilitadas en el servidor. Puedes consultar los equipos.</p>}
        {data && !rows.length && <div className="coord__empty"><strong>{projectId ? 'No hay agentes registrados para este proyecto en tu alcance.' : 'Todavía no hay agentes registrados.'}</strong><p>Conecta un equipo con Composer para ver sus agentes aquí.</p></div>}
        <ul className="coord-fleet__machines">
            {machines.map(machine => <MachineCard key={machine.key} machine={machine}
                groups={projectGroups(machine.visibleRows, repositoryList, projectList)} repositories={repositoryList}
                reviewWork={reviews.data} observedAt={data?.observed_at} disabled={disabled || projectId != null && catalogIncomplete}
                filtered={projectId != null} onGroup={openGroup}
                onSingle={(row, action) => setActing({ action: { ...action, target: row.position_id }, item: { row } })} />)}
        </ul>
        <details className="coord__setup"><summary>Cómo añadir agentes a un equipo</summary>
            <p>Registra las identidades con Composer en el computador y mantén su proceso encendido. Las claves se guardan allí. En este panel aparecerán los agentes registrados, agrupados por proyecto; después podrás autorizarlos juntos.</p>
            <p>Conectado significa que el canal responde. El trabajo activo y la confirmación de órdenes se muestran por separado en cada agente.</p>
        </details>
        {acting && <ActionModal {...acting} onClose={() => setActing(null)} />}
        {batch && <BatchModal {...batch} onClose={() => setBatch(null)} />}
    </section>;
}
